import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import type { AllowlistEntryResponse, CreateAllowlistEntryRequest, UpdateAllowlistEntryRequest } from '@/services/api'
import { Card, Table, Pagination, Badge, Button, Input, Select, Modal } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatDate } from '@/utils/format-date'

type SignalType = 'referrer' | 'user_agent' | 'ip_range'

const SIGNAL_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'referrer', label: 'Referrer' },
  { value: 'user_agent', label: 'User Agent' },
  { value: 'ip_range', label: 'IP Range' },
]

const SIGNAL_TYPE_BADGE_MAP: Record<string, { label: string; variant: 'primary' | 'success' | 'warning' }> = {
  referrer: { label: 'Referrer', variant: 'primary' },
  user_agent: { label: 'User Agent', variant: 'success' },
  ip_range: { label: 'IP Range', variant: 'warning' },
}

type FormState = {
  signalType: SignalType
  pattern: string
  description: string
}

const EMPTY_FORM: FormState = { signalType: 'referrer', pattern: '', description: '' }

function validateRegex(pattern: string): string | null {
  if (!pattern.trim()) return 'Pattern is required'
  try {
    new RegExp(pattern)
    return null
  } catch {
    return 'Invalid regular expression'
  }
}

export function AdminAllowlistPage() {
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editEntry, setEditEntry] = useState<AllowlistEntryResponse | null>(null)
  const [deleteEntry, setDeleteEntry] = useState<AllowlistEntryResponse | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [patternError, setPatternError] = useState<string | null>(null)

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-allowlist', typeFilter, offset],
    queryFn: () =>
      api.adminListAllowlistEntries({
        signalType: typeFilter || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
  })

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['admin-allowlist'] })
  }, [queryClient])

  const createEntry = useMutation({
    mutationFn: (data: CreateAllowlistEntryRequest) => api.adminCreateAllowlistEntry(data),
    onSuccess: () => { invalidate(); closeAddModal() },
  })

  const updateEntry = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAllowlistEntryRequest }) =>
      api.adminUpdateAllowlistEntry(id, data),
    onSuccess: () => { invalidate(); closeEditModal() },
  })

  const deleteEntryMutation = useMutation({
    mutationFn: (id: string) => api.adminDeleteAllowlistEntry(id),
    onSuccess: () => { invalidate(); setDeleteEntry(null) },
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.adminUpdateAllowlistEntry(id, { isActive }),
    onSuccess: invalidate,
  })

  function closeAddModal() {
    setShowAddModal(false)
    setForm(EMPTY_FORM)
    setPatternError(null)
  }

  function closeEditModal() {
    setEditEntry(null)
    setForm(EMPTY_FORM)
    setPatternError(null)
  }

  function openEditModal(entry: AllowlistEntryResponse) {
    setEditEntry(entry)
    setForm({
      signalType: entry.signalType,
      pattern: entry.pattern,
      description: entry.description,
    })
    setPatternError(null)
  }

  function handleSubmitAdd() {
    const err = validateRegex(form.pattern)
    if (err) { setPatternError(err); return }
    createEntry.mutate(form)
  }

  function handleSubmitEdit() {
    if (!editEntry) return
    const err = validateRegex(form.pattern)
    if (err) { setPatternError(err); return }
    updateEntry.mutate({
      id: editEntry.id,
      data: {
        signalType: form.signalType,
        pattern: form.pattern,
        description: form.description,
      },
    })
  }

  const columns = [
    {
      key: 'pattern',
      header: 'Pattern',
      render: (e: AllowlistEntryResponse) => (
        <span className="font-mono text-sm break-all">{e.pattern}</span>
      ),
    },
    {
      key: 'signalType',
      header: 'Signal Type',
      render: (e: AllowlistEntryResponse) => {
        const badge = SIGNAL_TYPE_BADGE_MAP[e.signalType]
        return <Badge variant={badge.variant}>{badge.label}</Badge>
      },
    },
    {
      key: 'description',
      header: 'Description',
      render: (e: AllowlistEntryResponse) => (
        <span className="text-sm text-gray-600">{e.description}</span>
      ),
    },
    {
      key: 'isActive',
      header: 'Active',
      render: (e: AllowlistEntryResponse) => (
        <button
          type="button"
          onClick={() => { toggleActive.mutate({ id: e.id, isActive: !e.isActive }) }}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            e.isActive ? 'bg-primary-600' : 'bg-gray-200'
          }`}
          role="switch"
          aria-checked={e.isActive}
          aria-label={`Toggle active for ${e.pattern}`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              e.isActive ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (e: AllowlistEntryResponse) => formatDate(e.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (e: AllowlistEntryResponse) => (
        <div className="flex gap-2">
          <button
            onClick={() => { openEditModal(e) }}
            className="text-gray-400 hover:text-primary-600"
            aria-label={`Edit ${e.pattern}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => { setDeleteEntry(e) }}
            className="text-gray-400 hover:text-danger-600"
            aria-label={`Delete ${e.pattern}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Global Allowlist</h1>
        <Button onClick={() => { setShowAddModal(true) }}>Add Entry</Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Select
            label="Signal Type"
            name="signalType"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value)
              setCurrentPage(1)
            }}
            options={SIGNAL_TYPE_OPTIONS}
            className="w-48"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load allowlist entries</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(e) => e.id}
              isLoading={isLoading}
              emptyMessage="No allowlist entries found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="Add Allowlist Entry"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={closeAddModal}>Cancel</Button>
            <Button isLoading={createEntry.isPending} onClick={handleSubmitAdd}>
              Add Entry
            </Button>
          </div>
        }
      >
        <AllowlistForm
          form={form}
          setForm={setForm}
          patternError={patternError}
          setPatternError={setPatternError}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editEntry !== null}
        onClose={closeEditModal}
        title="Edit Allowlist Entry"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={closeEditModal}>Cancel</Button>
            <Button isLoading={updateEntry.isPending} onClick={handleSubmitEdit}>
              Save Changes
            </Button>
          </div>
        }
      >
        <AllowlistForm
          form={form}
          setForm={setForm}
          patternError={patternError}
          setPatternError={setPatternError}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteEntry !== null}
        onClose={() => { setDeleteEntry(null) }}
        title="Delete Allowlist Entry"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setDeleteEntry(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={deleteEntryMutation.isPending}
              onClick={() => { if (deleteEntry) deleteEntryMutation.mutate(deleteEntry.id) }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to delete the allowlist entry with pattern{' '}
          <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">{deleteEntry?.pattern}</code>?
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  )
}

type AllowlistFormProps = {
  form: FormState
  setForm: (form: FormState) => void
  patternError: string | null
  setPatternError: (error: string | null) => void
}

function AllowlistForm({ form, setForm, patternError, setPatternError }: AllowlistFormProps) {
  return (
    <div className="space-y-4">
      <Select
        label="Signal Type"
        name="form-signalType"
        value={form.signalType}
        onChange={(e) => { setForm({ ...form, signalType: e.target.value as SignalType }) }}
        options={[
          { value: 'referrer', label: 'Referrer' },
          { value: 'user_agent', label: 'User Agent' },
          { value: 'ip_range', label: 'IP Range' },
        ]}
      />
      <Input
        label="Pattern (regex)"
        name="form-pattern"
        value={form.pattern}
        onChange={(e) => {
          setForm({ ...form, pattern: e.target.value })
          setPatternError(null)
        }}
        onBlur={() => {
          if (form.pattern) setPatternError(validateRegex(form.pattern))
        }}
        error={patternError ?? undefined}
        placeholder="e.g. ^https?://web\\.archive\\.org/"
      />
      <Input
        label="Description"
        name="form-description"
        value={form.description}
        onChange={(e) => { setForm({ ...form, description: e.target.value }) }}
        placeholder="e.g. Internet Archive Wayback Machine"
      />
    </div>
  )
}

